import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import type { SocialPost } from '../../types';
import { useLang } from '../../i18n/context';

export function SocialTab() {
  const { t, lang } = useLang();
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState<string>('');

  useEffect(() => { loadPosts(); }, [platform]);

  async function loadPosts() {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (platform) params.platform = platform;
      const data = await api.getSocialPosts(params);
      setPosts(data);
    } catch (err) {
      console.error('Failed to load posts:', err);
    } finally {
      setLoading(false);
    }
  }

  const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-BE';

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div>
      <div className="section-header">
        <span className="section-title">{t('social.title')}</span>
      </div>

      <div className="filter-bar" style={{ marginBottom: '16px' }}>
        <button className={`filter-chip ${!platform ? 'active' : ''}`} onClick={() => setPlatform('')}>{t('common.all')}</button>
        <button className={`filter-chip ${platform === 'instagram' ? 'active' : ''}`} onClick={() => setPlatform('instagram')}>Instagram</button>
        <button className={`filter-chip ${platform === 'facebook' ? 'active' : ''}`} onClick={() => setPlatform('facebook')}>Facebook</button>
      </div>

      {loading ? (
        <div className="loading"><span className="spinner"></span> {t('common.loading')}</div>
      ) : posts.length === 0 ? (
        <div className="empty-state">
          <p>{t('social.no_posts')}</p>
          <p style={{ fontSize: '13px', marginTop: '8px' }}>{t('social.launch_scan')}</p>
        </div>
      ) : (
        posts.map(post => (
          <div key={post.id} className="social-post">
            <div className="social-post-header">
              <span className={`social-platform-badge ${post.platform}`}>{post.platform}</span>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{post.source_name}</span>
              {post.posted_at && <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: 'auto' }}>{formatDate(post.posted_at)}</span>}
            </div>
            <div className="social-post-content">{post.content}</div>
            {post.image_urls?.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                {post.image_urls.map((url, i) => (
                  <img key={i} src={url} alt="" style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '6px', border: '1px solid var(--border-color)' }} />
                ))}
              </div>
            )}
            {post.post_url && (
              <a href={post.post_url} target="_blank" rel="noopener noreferrer"
                 style={{ display: 'inline-block', marginTop: '8px', fontSize: '12px' }}>
                {t('social.view_post')} &#8599;
              </a>
            )}
          </div>
        ))
      )}
    </div>
  );
}
